import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { WORK_ORDER_PRIORITIES, WORK_ORDER_STATUSES } from 'shared';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';
import { useCan } from '../../../lib/capabilities.js';

export interface WorkOrderRow {
  id: string;
  unitLabel: string;
  propertyName: string;
  vendorName: string | null;
  title: string;
  priority: string;
  status: string;
  scheduledOn: string | null;
}

const OPEN_STATUSES = new Set(['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'ON_HOLD']);

const columns: Column<WorkOrderRow>[] = [
  {
    key: 'title',
    header: 'Work order',
    render: (row) => (
      <>
        <strong>{row.title}</strong>
        <div className="muted">
          {row.unitLabel}, {row.propertyName}
        </div>
      </>
    ),
  },
  {
    key: 'priority',
    header: 'Priority',
    render: (row) => (
      <span className={row.priority === 'EMERGENCY' ? 'pill pill--late' : 'pill'}>
        {humanizeEnum(row.priority)}
      </span>
    ),
  },
  { key: 'status', header: 'Status', render: (row) => humanizeEnum(row.status) },
  { key: 'vendor', header: 'Vendor', render: (row) => row.vendorName ?? '—' },
  { key: 'scheduled', header: 'Scheduled', render: (row) => row.scheduledOn ?? '—' },
];

export function WorkOrderList() {
  const navigate = useNavigate();
  const canWrite = useCan('maintenance:write');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const pageSize = 25;

  const { data, isLoading, error } = useList<WorkOrderRow>({
    resource: 'work-orders',
    pagination: { current: page, pageSize },
    filters: [
      { field: 'status', operator: 'eq', value: status || undefined },
      { field: 'priority', operator: 'eq', value: priority || undefined },
    ],
  });

  const rows = data?.data ?? [];
  const openCount = rows.filter((r) => OPEN_STATUSES.has(r.status)).length;

  return (
    <section>
      <div className="row">
        <h1>Work orders</h1>
        <span className="shell__spacer" />
        {canWrite ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate('/staff/work-orders/new')}
          >
            New work order
          </button>
        ) : null}
      </div>

      {!status && !priority ? (
        <p className="muted">
          {openCount} open of {rows.length} shown.
        </p>
      ) : null}

      <div className="row">
        <select
          className="field__input"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Any status</option>
          {WORK_ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanizeEnum(s)}
            </option>
          ))}
        </select>

        <select
          className="field__input"
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Any priority</option>
          {WORK_ORDER_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {humanizeEnum(p)}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        onRowClick={(row) => void navigate(`/staff/work-orders/${row.id}`)}
        emptyMessage="No work orders yet."
        pagination={{ page, pageSize, total: data?.total ?? 0, onPageChange: setPage }}
      />
    </section>
  );
}
